import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
	Button,
	IconButton,
	ListItem,
	Dialog,
	DialogContent,
	DialogActions,
	Typography,
	TextField,
	Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { lprService, organizationService } from "client-app-core";
import { useSelector, useDispatch } from "react-redux";
import { openDialog, closeDialog } from "orion-components/AppState/Actions";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { getDir } from "orion-components/i18n/Config/selector";
import { getScrollBarAppState } from "orion-components/AppState/Global/selectors";
import { Translate, getTranslation } from "orion-components/i18n";
import { canManageByApplication } from "orion-components/Profiles/Selectors";
import { getWatchlistEntries } from "../actions";
import moment from "moment";

const entryFields = [
	{ label: "plateNumber", key: "plateNumber", minWidth: 65, contentWidth: 65 },
	{ label: "plateState", key: "state", minWidth: 65, contentWidth: 65 },
	{ label: "dateAdded", key: "createdDate", minWidth: 71, contentWidth: 70 },
	{ label: "addedBy", key: "createdBy", minWidth: 71, contentWidth: 70 }
];

const baseStyles = {
	entriesListItem: {
		display: "flex",
		alignItems: "center",
		gap: "10px",
		flex: "1 0 0"
	},
	hotListFieldWrapper: (dir, params = {}) => {
		const includeFlex = params.includeFlex !== false;
		return {
			display: "flex",
			flexDirection: "column",
			justifyContent: "flex-end",
			alignItems: "flex-start",
			gap: "5px",
			...(includeFlex && { flex: "1 0 0" })
		};
	},
	hotListFieldHeader: {
		color: "#B5B9BE",
		fontFamily: "Roboto",
		fontSize: "10px",
		fontStyle: "normal",
		fontWeight: 400,
		lineHeight: "normal",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		overflow: "hidden"
	},
	hotListValues: {
		color: "#FFF",
		fontFamily: "Roboto",
		fontSize: "12px",
		fontStyle: "normal",
		fontWeight: 400,
		lineHeight: "normal",
		overflow: "hidden",
		width: "100%",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap"
	},
	removeButton: { display: "flex", alignItems: "center", gap: 10, color: "#fff" },
	textFieldInputLabelProps: {
		fontFamily: "Roboto",
		fontSize: "12px",
		color: "#aaa"
	},
	standardTextFieldInputProps: {
		sx: {
			fontFamily: "Roboto",
			fontWeight: 400,
			fontSize: "14px",
			lineHeight: "100%",
			letterSpacing: "0",
			textAlign: "center",
			color: "#FFF",
			"& input": {
				padding: "0px 0px 10px 0px"
			},
			"&:before": {
				borderBottom: "1px solid #949597"
			},
			"&:after": {
				borderBottom: "1px solid #42a5f5"
			},
			"&:hover:not(.Mui-disabled):before": {
				borderBottom: "1px solid #42a5f5"
			}
		},
		autoComplete: "off"
	},
	entryTextFieldSx: {
		input: {
			color: "#fff",
			fontFamily: "Roboto",
			fontSize: "12px",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "normal"
		},
		label: {
			color: "rgba(255, 255, 255, 0.3)",
			fontFamily: "Roboto",
			fontSize: "12px",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "normal"
		},
		flex: 1
	},
	stateEntryTextFieldSx: {
		input: {
			color: "#fff",
			fontFamily: "Roboto",
			fontSize: "12px",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "normal"
		},
		label: {
			color: "rgba(255, 255, 255, 0.3)",
			fontFamily: "Roboto",
			fontSize: "12px",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "normal"
		},
		"& .MuiFormLabel-asterisk": {
			color: "#E85858",
			fontFamily: "Roboto",
			fontSize: "12px",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "normal",
			leadingTrim: "both",
			textEdge: "cap"
		},
		flex: 1
	},
	addEntryButton: {
		width: "80px",
		height: "35px",
		backgroundColor: "#4CB5F4",
		fontSize: "12px",
		fontWeight: 700,
		fontFamily: "Roboto",
		borderRadius: "5px",
		alignSelf: "center",
		color: "#FFFFFF",
		":hover": {
			backgroundColor: "#42a5f5"
		},
		textTransform: "uppercase",
		lineHeight: "1.2"
	},
	entriesHeader: locale => ({
		fontFamily: "Roboto",
		fontStyle: "normal",
		fontWeight: 400,
		fontSize: "14px",
		lineHeight: "16px",
		color: "#FFFFFF",
		alignSelf: locale === "ar" || locale === "ar_kw" ? "end" : "start",
		overflow: "hidden",
		width: "100%",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap"
	}),
	backButtonSx: {
		paddingRight: "5px",
		color: "#fff"
	},
	entryFormStack: { display: "flex", flexDirection: "row", gap: "10px", margin: "0 13px" },
	entriesContainer: scrollBarsEnabled => ({
		marginTop: 16,
		overflowY: "scroll",
		marginLeft: 13,
		...(!scrollBarsEnabled ? { marginRight: 13 } : {})
	}),
	entryListItemStyle: {
		height: 52,
		backgroundColor: "#494D53",
		borderRadius: 5,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		flexShrink: 0,
		marginBottom: 10,
		padding: "8px 48px 8px 8px"
	},
	hotListFieldItemStyle: (wrapperStyle, field) => ({
		...wrapperStyle,
		minWidth: field.minWidth,
		width: field.width
	}),
	hotListFieldHeaderStyle: field => ({
		...baseStyles.hotListFieldHeader,
		width: field.width
	}),
	hotListFieldValueStyle: field => ({
		...baseStyles.hotListValues,
		width: field.contentWidth
	}),
	emptyStateMessage: { padding: 20, textAlign: "center", color: "white" },
	deleteDialogPaperSx: {
		backgroundColor: "#41454a",
		padding: "20px",
		borderRadius: 0,
		width: "350px",
		height: "225px"
	},
	whiteTypography: { color: "#fff" },
	dialogButton: { height: 45, color: "#fff" },
	deleteConfirmButtonSx: {
		backgroundColor: "#f44336",
		textTransform: "uppercase",
		":hover": {
			backgroundColor: "#E95858"
		}
	},
	listItemHoverActions: {
		".MuiListItemSecondaryAction-root": {
			opacity: 0,
			visibility: "hidden",
			transition: "opacity 0.2s ease, visibility 0.2s ease",
			marginRight: "-8px"
		},
		"&:hover .MuiListItemSecondaryAction-root": {
			opacity: 1,
			visibility: "visible"
		}
	}
};

const EntriesView = ({ currentWatchlist, onClose }) => {
	const dispatch = useDispatch();
	const textRefs = useRef({});

	const canManageLPR = useSelector(state => canManageByApplication(state, "lpr-app", "manage"));
	const dir = useSelector(state => getDir(state));
	const locale = useSelector(state => state.i18n.locale);
	const user = useSelector(state => state.session.user);
	const scrollBarsEnabled = useSelector(state => getScrollBarAppState(state));

	const dialog = useSelector(state => state.appState.dialog.openDialog);
	const entries = useSelector(state => state.appState.dock.dockData.watchlistEntries);

	const styles = useMemo(() => generateDirStyles(baseStyles, dir), [dir]);

	const [error, setError] = useState("");
	const [errorContext, setErrorContext] = useState("");
	const [plateNum, setPlateNum] = useState("");
	const [plateState, setPlateState] = useState("");
	const [currentEntry, setCurrentEntry] = useState(null);
	const [orgUserNames, setOrgUserNames] = useState(() => new Map());
	const [tooltipStates, setTooltipStates] = useState({});

	const isArabicLocale = useMemo(() => locale === "ar" || locale === "ar_kw", [locale]);

	const sortedEntries = useMemo(
		() =>
			[...(entries || [])].sort((a, b) => {
				const plateA = a.plateNumber.toUpperCase();
				const plateB = b.plateNumber.toUpperCase();
				return plateA.localeCompare(plateB);
			}),
		[entries]
	);

	const hotListWrapperStyles = useMemo(
		() =>
			entryFields.map(
				(_, index) =>
					generateDirStyles(
						{
							wrapper: baseStyles.hotListFieldWrapper(dir, {
								includeFlex: index !== entryFields.length - 1
							})
						},
						dir
					).wrapper
			),
		[dir]
	);

	const getHotListWrapperStyle = useCallback(index => hotListWrapperStyles[index], [hotListWrapperStyles]);

	useEffect(() => {
		dispatch(getWatchlistEntries(currentWatchlist.id));
	}, [dispatch]);

	useEffect(() => {
		try {
			organizationService.getOrgUsers(user.profile.orgId, (err, response) => {
				if (err) {
					console.error(err);
				} else {
					setOrgUserNames(new Map(response.map(({ id, name }) => [id, name])));
				}
			});
		} catch (err) {
			console.error(err);
		}
	}, [user.profile.orgId]);

	useEffect(() => {
		// Clears the entry fields and any errors/status messages when component mounts
		setPlateNum("");
		setPlateState("");
		setError("");
		setErrorContext("");
	}, []);

	useEffect(() => {
		// Check overflow for all text elements
		const checkAllOverflows = () => {
			const newTooltipStates = {};
			Object.entries(textRefs.current).forEach(([refKey, element]) => {
				if (element) {
					newTooltipStates[refKey] = element.scrollWidth > element.clientWidth;
				}
			});
			setTooltipStates(newTooltipStates);
		};

		checkAllOverflows();
		window.addEventListener("resize", checkAllOverflows);
		return () => window.removeEventListener("resize", checkAllOverflows);
	}, [sortedEntries]);

	const addNewWatchlistEntry = (plateNumber, state) => {
		if (!plateNumber || !plateNumber.trim()) {
			setError("emptyPlateNumber");
			setErrorContext("plateNumberEntry");
			return;
		}
		if (!state || !state.trim()) {
			setError("emptyStateProvince");
			setErrorContext("stateEntry");
			return;
		}
		setError("");
		setErrorContext("");
		const watchlistId = currentWatchlist.id;
		lprService.addWatchlistEntry(watchlistId, plateNumber.trim(), state.trim(), (err, res) => {
			if (err) {
				setError(err.reason);
				setErrorContext("plateNumberEntry");
			} else {
				if (res.success === false) {
					setError(res.reason.message);
					setErrorContext("plateNumberEntry");
				} else {
					setPlateNum("");
					setPlateState("");
					dispatch(getWatchlistEntries(watchlistId));
				}
			}
		});
	};

	const openDeleteEntryDialog = entry => {
		setCurrentEntry(entry);
		dispatch(openDialog("confirmDeleteEntry"));
	};

	const deleteEntry = id => {
		if (!id) {
			console.error("Missing ID for deletion");
			return;
		}
		lprService.removeEntryFromWatchlist(currentWatchlist.id, id, (err, res) => {
			if (err) {
				console.error(err.message, err);
			} else {
				dispatch(closeDialog("confirmDeleteEntry"));
				setCurrentEntry(null);
				dispatch(getWatchlistEntries(currentWatchlist.id));
			}
		});
	};

	return (
		<>
			<p style={styles.entriesHeader(locale)}>
				<IconButton aria-label="back" onClick={onClose} sx={styles.backButtonSx} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-icon-button">
					{isArabicLocale ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />}
				</IconButton>
				{getTranslation("global.sharedComponents.LPRWatchlistPanel.entriesHeader") + currentWatchlist.name}
			</p>
			{canManageLPR && (
				<div style={styles.entryFormStack}>
					<TextField
						label={getTranslation(`global.sharedComponents.LPRWatchlistPanel.plateEntry`)}
						value={plateNum}
						error={errorContext === "plateNumberEntry"}
						helperText={
							errorContext === "plateNumberEntry" && error
								? getTranslation(`global.sharedComponents.LPRWatchlistPanel.error.${error}`)
								: ""
						}
						onChange={event => {
							setPlateNum(event.target.value);
							if (errorContext === "plateNumberEntry") {
								setErrorContext("");
								setError("");
							}
						}}
						variant="standard"
						InputProps={styles.standardTextFieldInputProps}
						InputLabelProps={{
							sx: styles.textFieldInputLabelProps
						}}
						sx={styles.entryTextFieldSx} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-text-field"
					/>
					<TextField
						label={getTranslation(`global.sharedComponents.LPRWatchlistPanel.plateState`)}
						value={plateState}
						error={errorContext === "stateEntry"}
						helperText={
							errorContext === "stateEntry" && error
								? getTranslation(`global.sharedComponents.LPRWatchlistPanel.error.${error}`)
								: ""
						}
						onChange={event => {
							setPlateState(event.target.value);
							if (errorContext === "stateEntry") {
								setErrorContext("");
								setError("");
							}
						}}
						variant="standard"
						InputProps={styles.standardTextFieldInputProps}
						InputLabelProps={{
							sx: styles.textFieldInputLabelProps
						}}
						sx={styles.stateEntryTextFieldSx} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-text-field-2"
					/>
					<Button
						onClick={() => addNewWatchlistEntry(plateNum, plateState)}
						variant="contained"
						sx={styles.addEntryButton} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-button"
					>
						<Translate value="global.sharedComponents.LPRWatchlistPanel.addEntry" />
					</Button>
				</div>
			)}
			{sortedEntries.length > 0 ? (
				<div style={styles.entriesContainer(scrollBarsEnabled)}>
					{sortedEntries.map(entry => (
						<ListItem
							style={styles.entryListItemStyle}
							sx={canManageLPR ? styles.listItemHoverActions : undefined}
							key={entry.id || `${entry.plateNumber}-${entry.createdDate}`}
							secondaryAction={
								canManageLPR && (
									<IconButton sx={styles.removeButton} onClick={() => openDeleteEntryDialog(entry)} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-icon-button-2">
										<DeleteIcon />
									</IconButton>
								)
							} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-list-item"
						>
							<div style={styles.entriesListItem}>
								{entryFields.map((field, fieldIndex) => (
									<div
										key={field.key}
										style={styles.hotListFieldItemStyle(getHotListWrapperStyle(fieldIndex), field)}
									>
										<div style={styles.hotListFieldHeaderStyle(field)}>
											{getTranslation(`global.sharedComponents.LPRWatchlistPanel.${field.label}`)}
										</div>
										{field.label === "plateNumber" || field.label === "plateState" ? (
											<Tooltip
												title={entry[field.key]}
												disableFocusListener
												placement="bottom-start"
												disableHoverListener={!tooltipStates[`${entry.id}-${field.key}`]}
												slotProps={{
													popper: {
														modifiers: [
															{
																name: "offset",
																options: {
																	offset: [0, -14]
																}
															}
														]
													}
												}} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-tooltip"
											>
												<div
													style={styles.hotListFieldValueStyle(field)}
													ref={el => {
														if (el) {
															textRefs.current[`${entry.id}-${field.key}`] = el;
														}
													}}
												>
													{entry[field.key]}
												</div>
											</Tooltip>
										) : field.label === "addedBy" ? (
											<Tooltip
												title={
													orgUserNames.get(entry[field.key]) ??
													getTranslation(
														`global.sharedComponents.LPRWatchlistPanel.unknownUser`
													)
												}
												disableFocusListener
												placement="bottom-start"
												disableHoverListener={!tooltipStates[`${entry.id}-${field.key}`]}
												slotProps={{
													popper: {
														modifiers: [
															{
																name: "offset",
																options: {
																	offset: [0, -14]
																}
															}
														]
													}
												}} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-tooltip-2"
											>
												<div
													style={styles.hotListFieldValueStyle(field)}
													ref={el => {
														if (el) {
															textRefs.current[`${entry.id}-${field.key}`] = el;
														}
													}}
												>
													{orgUserNames.get(entry[field.key]) ??
														getTranslation(
															`global.sharedComponents.LPRWatchlistPanel.unknownUser`
														)}
												</div>
											</Tooltip>
										) : (
											<div style={styles.hotListFieldValueStyle(field)}>
												{field.label === "dateAdded"
													? moment(entry[field.key]).locale(locale).format("MM/DD/YYYY") ||
														"N/A"
													: entry[field.key]}
											</div>
										)}
									</div>
								))}
							</div>
						</ListItem>
					))}
				</div>
			) : (
				<div style={styles.emptyStateMessage}>
					{getTranslation("global.sharedComponents.LPRWatchlistPanel.noWatchlistEntries")}
				</div>
			)}
			{canManageLPR && (
				<Dialog
					open={dialog === "confirmDeleteEntry"}
					PaperProps={{
						sx: styles.deleteDialogPaperSx
					}} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-dialog"
				>
					<DialogContent data-testid="orion-components-dock-lprwatchlist-panel-entries-view-dialog-content">
						<Typography style={styles.whiteTypography}>
							{getTranslation("global.sharedComponents.LPRWatchlistPanel.dialog.deleteEntryText")}
						</Typography>
					</DialogContent>
					<DialogActions data-testid="orion-components-dock-lprwatchlist-panel-entries-view-dialog-actions">
						<Button
							style={styles.dialogButton}
							variant="contained"
							sx={styles.deleteConfirmButtonSx}
							onClick={() => deleteEntry(currentEntry.id)} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-button-2"
						>
							<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.confirm" />
						</Button>
						<Button
							style={styles.dialogButton}
							color="primary"
							onClick={() => {
								dispatch(closeDialog("confirmDeleteEntry"));
								setCurrentEntry(null);
							}} data-testid="orion-components-dock-lprwatchlist-panel-entries-view-button-3"
						>
							<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.cancel" />
						</Button>
					</DialogActions>
				</Dialog>
			)}
		</>
	);
};

export default EntriesView;
